/**
 * Debounced, abortable price estimates — shared by the booking form (pricing a new booking as the
 * customer builds it) and the customer portal (pricing a reschedule before it is confirmed).
 *
 * The caller supplies `build` and `current` as thunks rather than values, because the request is
 * deliberately re-derived when the timer fires: state can move between scheduling and firing, and
 * comparing the re-derived key against the scheduled one is what stops a stale response landing.
 */
import type { ApiClient } from '../api/client';
import { ApiError, isAbortError, type FieldErrors } from '../api/errors';
import type { EstimatePriceRequest, PriceEstimate } from '../api/types';
import { estimateKey } from '../domain/payload';
import { GENERIC, customerMessage, scrubFieldErrors } from '../domain/messages';

export type EstimateStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface EstimatorDeps {
  client: Pick<ApiClient, 'estimatePrice'>;
  /** Re-derived on every schedule and again when the timer fires. */
  build: () => EstimatePriceRequest | null;
  /** The estimate slice as it stands now, for deduping an already-settled request. */
  current: () => { key: string | null; status: EstimateStatus };
  debounceMs?: number;
  onLoading: (key: string) => void;
  onOk: (key: string, data: PriceEstimate) => void;
  onFail: (key: string, message: string, fieldErrors: FieldErrors) => void;
  /** Nothing is priceable any more (e.g. the last service was removed). */
  onCleared: () => void;
}

export type Estimator = ReturnType<typeof createEstimator>;

export function createEstimator(deps: EstimatorDeps) {
  const debounceMs = deps.debounceMs ?? 400;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let abort: AbortController | null = null;

  async function run(scheduledKey: string): Promise<void> {
    const request = deps.build();
    // State moved on between scheduling and firing — a newer schedule owns the result.
    if (!request || estimateKey(request) !== scheduledKey) return;

    abort?.abort();
    const ctrl = (abort = new AbortController());
    deps.onLoading(scheduledKey);
    try {
      const data = await deps.client.estimatePrice(request, ctrl.signal);
      if (ctrl.signal.aborted) return;
      deps.onOk(scheduledKey, data);
    } catch (err) {
      if (isAbortError(err)) return;
      const fieldErrors = err instanceof ApiError ? scrubFieldErrors(err.fieldErrors) : {};
      deps.onFail(scheduledKey, customerMessage(err, GENERIC.failed), fieldErrors);
      // The code the customer typed was rejected; re-price without it rather than showing a
      // total that silently still assumes the discount.
      if (fieldErrors['discount_code']) schedule();
    }
  }

  function schedule(): void {
    if (timer) clearTimeout(timer);
    const request = deps.build();
    const key = estimateKey(request);
    if (!key || !request) {
      abort?.abort();
      if (deps.current().status !== 'idle') deps.onCleared();
      return;
    }
    const current = deps.current();
    if (key === current.key && current.status === 'ready') return;
    timer = setTimeout(() => void run(key), debounceMs);
  }

  return {
    schedule,
    destroy(): void {
      if (timer) clearTimeout(timer);
      timer = null;
      abort?.abort();
      abort = null;
    },
  };
}
